import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import LocalizedContentFields from "../LocalizedContentFields/LocalizedContentFields";
import { emptyContentTranslations } from "../../i18n/contentTranslationApi";

function AddonForm() {
  const dispatch = useDispatch();
  const editData = useSelector((state) => state.admin?.editAddonGroup);
  const addonSaving = useSelector((state) => Boolean(state.admin?.addonSaving));
  const restaurantId = useSelector((state) => state.user?.restaurantId || state.admin?.restaurantId);

  const [groupName, setGroupName] = useState("");
  const [minSelection, setMinSelection] = useState(0);
  const [maxSelection, setMaxSelection] = useState(1);
  const [addonItems, setAddonItems] = useState([
    { itemName: "", price: "" }
  ]);
  const [errors, setErrors] = useState({});
  const [translations, setTranslations] = useState(emptyContentTranslations);
  const submitLock = useRef(false);

  useEffect(() => {
    if (!addonSaving) submitLock.current = false;
  }, [addonSaving]);

  useEffect(() => {
    if (editData && editData.addonGroupId) {
      setGroupName(editData.groupName || "");
      setMinSelection(editData.minSelection || 0);
      setMaxSelection(editData.maxSelection || 1);
      if (editData.addonItems && editData.addonItems.length > 0) {
        setAddonItems(
          editData.addonItems.map((item) => ({
            addonItemId: item.addonItemId,
            itemName: item.itemName,
            price: item.price
          }))
        );
      }
    } else {
      // Reset form for fresh add
      setGroupName("");
      setMinSelection(0);
      setMaxSelection(1);
      setAddonItems([{ itemName: "", price: "" }]);
    }
    setErrors({});
  }, [editData]);

  // Dynamic row: Add new item
  const handleAddItemRow = () => {
    setAddonItems([...addonItems, { itemName: "", price: "" }]);
  };

  // Dynamic row: Remove item
  const handleRemoveItemRow = (index) => {
    if (addonItems.length === 1) return;
    const updated = addonItems.filter((_, idx) => idx !== index);
    setAddonItems(updated);
    if (maxSelection > updated.length) {
      setMaxSelection(updated.length);
    }
  };

  // Dynamic row: Update name or price
  const handleItemChange = (index, field, value) => {
    const updated = [...addonItems];
    updated[index][field] = value;
    setAddonItems(updated);
  };

  const validateForm = () => {
    const errs = {};
    if (!groupName.trim()) {
      errs.groupName = "Group name is required.";
    }

    const validItems = addonItems.filter((i) => i.itemName.trim() !== "");
    if (validItems.length === 0) {
      errs.items = "At least one item is required.";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (addonSaving || submitLock.current) return;
    if (!validateForm()) return;
    submitLock.current = true;

    const payload = {
      addonGroupId: editData?.addonGroupId || null,
      restaurantId: restaurantId,
      groupName: groupName.trim(),
      description: "",
      minSelection: Number(minSelection),
      maxSelection: Number(maxSelection),
      addonItems: addonItems
        .filter((i) => i.itemName.trim() !== "")
        .map((i) => ({
          addonItemId: i.addonItemId || null,
          itemName: i.itemName.trim(),
          price: parseFloat(i.price) || 0
        })),
      translations
    };

    // Redux action dispatch (create/update)
    dispatch({ type: "SAVE_ADDON_GROUP", payload });
  };

  return (
    <form id="addon-form" onSubmit={handleSubmit} style={styles.form}>
      {/* Group Name */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Name</label>
        <input
          type="text"
          placeholder="e.g. Toppings Veg, Cheese & Dip"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          style={styles.input}
        />
        {errors.groupName && <p style={styles.errorText}>{errors.groupName}</p>}
      </div>

      {/* Dynamic Add-on Items list */}
      <div style={styles.itemsContainer}>
        <label style={styles.label}>Add Add-on's Name & Price</label>

        {addonItems.map((item, index) => (
          <div key={index} style={styles.row}>
            <input
              type="text"
              placeholder="Name (e.g. Paneer)"
              value={item.itemName}
              onChange={(e) => handleItemChange(index, "itemName", e.target.value)}
              style={{ ...styles.input, flex: 2 }}
            />
            <input
              type="number"
              placeholder="Price (₹)"
              value={item.price}
              min="0"
              onChange={(e) => handleItemChange(index, "price", e.target.value)}
              style={{ ...styles.input, flex: 1 }}
            />
            {addonItems.length > 1 && (
              <span
                style={styles.deleteRowBtn}
                title="Remove item"
                onClick={() => handleRemoveItemRow(index)}
              >
                ✕
              </span>
            )}
          </div>
        ))}

        {errors.items && <p style={styles.errorText}>{errors.items}</p>}

        {/* Add option button */}
        <button
          type="button"
          style={styles.addOptionBtn}
          onClick={handleAddItemRow}
        >
          + Add Item / Option
        </button>
      </div>

      <LocalizedContentFields
        entityType="addon-group"
        entityId={editData?.addonGroupId}
        value={translations}
        onChange={setTranslations}
        fields={[
          { name: "name", label: "Translated group name", placeholder: "Enter translated add-on group name" },
          { name: "description", label: "Translated description", placeholder: "Enter translated description", multiline: true }
        ]}
      />

      {/* Max selection limit */}
      <div style={styles.formGroup}>
        <label style={styles.label}>
          Total {addonItems.filter((i) => i.itemName.trim()).length} options: How many can customer select?
        </label>
        <select
          value={maxSelection}
          onChange={(e) => setMaxSelection(Number(e.target.value))}
          style={styles.select}
        >
          {Array.from({ length: Math.max(addonItems.length, 1) }, (_, i) => i + 1).map((num) => (
            <option key={num} value={num}>
              {num}
            </option>
          ))}
        </select>
      </div>

    </form>
  );
}

const styles = {
  form: { display: "flex", flexDirection: "column", gap: "16px", padding: "10px 0" },
  formGroup: { display: "flex", flexDirection: "column", gap: "6px", textAlign: "left" },
  label: { fontWeight: "600", fontSize: "14px", color: "#333" },
  input: {
    padding: "10px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "14px",
    boxSizing: "border-box"
  },
  select: {
    padding: "10px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "14px",
    backgroundColor: "#fff"
  },
  itemsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    backgroundColor: "#f9fafb",
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb"
  },
  row: { display: "flex", gap: "10px", alignItems: "center" },
  deleteRowBtn: {
    color: "#ef4444",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
    padding: "4px 8px"
  },
  addOptionBtn: {
    backgroundColor: "#0284c7",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "10px",
    fontWeight: "600",
    cursor: "pointer",
    marginTop: "4px"
  },
  errorText: { color: "#dc2626", fontSize: "12px", marginTop: "2px" }
};

export default AddonForm;
